import { useState } from 'react'

/**
 * A table preference the person's browser remembers. Storage is best-effort on both sides: a
 * browser in private mode throws on read and on write, and a preference is never worth failing a
 * page render over - the choice simply lasts the session instead.
 *
 * `parse` receives whatever was stored, or null when nothing was, and returns the value to use. It
 * owns the default, because every preference reads a bad value differently: one falls back to a
 * company setting, another to a fixed literal.
 */
export function useStoredPreference<T>(key: string, parse: (stored: string | null) => T): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      return parse(localStorage.getItem(key))
    } catch {
      return parse(null)
    }
  })

  const store = (next: T) => {
    setValue(next)
    try {
      localStorage.setItem(key, String(next))
    } catch {
      // storage full or unavailable - the choice still applies for this session
    }
  }

  return [value, store]
}
